import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useSSEStore } from '../../stores/sseStore';
import { usePagination } from '../../hooks/usePagination';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { EntityLink } from '../../components/shared/EntityLink';
import { Pagination } from '../../components/shared/Pagination';
import { LinkPurchaseModal, GenericPurchaseModal } from './LinkPurchaseModal';
import { StockPOModal } from './StockPOModal';
import { ReportOrdersTab } from './ReportOrdersTab';
import { ReportShipmentsTab } from './ReportShipmentsTab';
import { useCan, visibleTabs, pickActiveTab, NoAccess, Can } from '../../lib/rbac';
import {
  Globe, Search, ShoppingCart, TrendingUp, Database,
  Package, Truck, DollarSign,
  Clock, AlertCircle, CheckCircle2, XCircle, Star,
  ArrowUpDown, ExternalLink, X, Loader2, Activity,
  BarChart3, Zap, Pause, Play, Eye, RotateCcw, Check, Link2,
  Pencil, Save, RefreshCw
} from 'lucide-react';

type Tab = 'purchases' | 'orders' | 'shipments' | 'price-monitor' | 'catalog';

const AMZ_STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  ordered: 'info',
  shipped: 'warning',
  delivered: 'ok',
  delivered_ml: 'ok',
  pending_approval: 'warning',
  cancelled: 'error',
  error: 'error',
  failed: 'error',
  unlinked: 'unknown',
  from_stock: 'ok',
  safeguard_hold: 'warning',
  needs_review: 'warning',
};

const PM_ACTION_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  repriced: 'info',
  paused: 'error',
  reactivated: 'ok',
  skipped: 'unknown',
  stock_zero: 'error',
  delivery_slow: 'warning',
  no_fba: 'warning',
  no_prime: 'warning',
  condition_mismatch: 'error',
  restocked: 'ok',
  initial_price: 'info',
  floored: 'info',
  capped: 'info',
};

export default function AmazonPage() {
  const { t } = useTranslation();
  const can = useCan();
  const [tab, setTab] = useState<Tab>('purchases');

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'purchases', label: t('amz.purchases'), icon: <ShoppingCart size={16} /> },
    { key: 'orders', label: 'Orders', icon: <Package size={16} /> },
    { key: 'shipments', label: 'Shipments', icon: <Truck size={16} /> },
    { key: 'price-monitor', label: t('amz.priceMonitor'), icon: <TrendingUp size={16} /> },
    { key: 'catalog', label: t('amz.catalog'), icon: <Database size={16} /> },
  ];

  const allowedTabs = visibleTabs('amazon', tabs, can);
  const active = pickActiveTab('amazon', tabs, tab, can) as Tab | null;
  useEffect(() => {
    if (active && active !== tab) setTab(active);
  }, [active, tab]);

  if (active === null) return <NoAccess />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Globe size={24} className="text-[var(--color-primary)]" />
        <h1 className="text-xl font-bold text-[var(--color-text)]">Amazon</h1>
      </div>

      <div className="flex gap-1 bg-[var(--color-bg-secondary)] p-1 rounded-lg w-fit">
        {allowedTabs.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              active === key
                ? 'bg-[var(--color-primary)] text-white'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)]'
            }`}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {active === 'purchases' && <PurchasesTab />}
      {active === 'orders' && <ReportOrdersTab />}
      {active === 'shipments' && <ReportShipmentsTab />}
      {active === 'price-monitor' && <PriceMonitorTab />}
      {active === 'catalog' && <CatalogTab />}
    </div>
  );
}

// ─────────────────────────────────────────────────
// PURCHASES TAB
// ─────────────────────────────────────────────────

function PurchasesTab() {
  const { t } = useTranslation();
  const can = useCan();
  const canMoney = can('global.view_money');
  const canOperate = can('amazon.operate');
  const { sellerId } = useUIStore();
  const { subscribe } = useSSEStore();
  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const pag = usePagination('amazon-purchases');
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Link, Generic purchase & Stock PO modals
  const [linkModalOrder, setLinkModalOrder] = useState<number | null>(null);
  const [genericModalOpen, setGenericModalOpen] = useState(false);
  const [stockPOModalOpen, setStockPOModalOpen] = useState(false);

  // PO state (inline, not separate tab)
  const [poList, setPOList] = useState<any[]>([]);
  const [poListOpen, setPOListOpen] = useState(false);
  const [activePO, setActivePO] = useState<any>(null);
  const [poItems, setPOItems] = useState<any[]>([]);
  const [poProgress, setPOProgress] = useState<{ current: number; total: number; results: any[] } | null>(null);

  const toggleSelect = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === data.length) setSelected(new Set());
    else setSelected(new Set(data.map(r => r.id)));
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        seller_id: String(sellerId),
        page: String(pag.page),
        per_page: String(pag.perPage),
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search) params.set('search', search);

      const [dataRes, statsRes] = await Promise.all([
        api.get<any[]>(`/amazon/purchases?${params}`),
        api.get<any>(`/amazon/purchases/stats?seller_id=${sellerId}`),
      ]);
      if (dataRes.success && dataRes.data) {
        setData(dataRes.data);
        pag.setTotal((dataRes as any).meta?.total || 0);
      }
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load purchases:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId, pag.page, pag.perPage, statusFilter, search]);

  useEffect(() => { loadData(); }, [loadData]);

  // SSE real-time updates
  const loadRef = useRef(loadData);
  loadRef.current = loadData;
  useEffect(() => {
    const refresh = () => loadRef.current();
    const unsubs = [
      subscribe('amazon:purchase_updated', refresh),
      subscribe('amazon:tracking_updated', refresh),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  // PO functions
  const loadPOs = useCallback(async () => {
    const res = await api.get<any[]>(`/amazon/po?seller_id=${sellerId}&limit=20`);
    if (res.success && res.data) setPOList(res.data);
  }, [sellerId]);

  useEffect(() => { loadPOs(); }, [loadPOs]);

  const openPODetail = async (po: any) => {
    const res = await api.get<any>(`/amazon/po/${po.id}`);
    if (res.success && res.data) { setActivePO(res.data); setPOItems(res.data.items || []); setPOProgress(null); }
  };

  const createPO = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const res = await api.post('/amazon/po', { seller_id: sellerId, purchase_ids: ids });
    if (res.success && res.data) {
      setSelected(new Set());
      loadData(); loadPOs();
      openPODetail(res.data);
    } else {
      alert((res as any).error || 'Error creating PO');
    }
  };

  const approvePO = async () => {
    if (!activePO || activePO.status !== 'draft') return;
    if (!confirm(`Approve PO ${activePO.po_number} and place ${activePO.item_count} real orders?`)) return;
    setPOProgress({ current: 0, total: activePO.item_count, results: [] });
    await api.post(`/amazon/po/${activePO.id}/approve?seller_id=${sellerId}`, {});
  };

  const stopPO = async () => { if (activePO) await api.post(`/amazon/po/${activePO.id}/stop`, {}); };
  const retryPO = async () => { if (activePO) { setPOProgress({ current: 0, total: 0, results: [] }); await api.post(`/amazon/po/${activePO.id}/retry?seller_id=${sellerId}`, {}); } };
  const releasePO = async () => { if (activePO) { await api.post(`/amazon/po/${activePO.id}/release`, {}); openPODetail(activePO); loadPOs(); loadData(); } };
  const cancelPO = async (poId: number) => { await api.delete(`/amazon/po/${poId}`); setActivePO(null); loadPOs(); loadData(); };

  // ── Individual Purchase Actions ──
  const approvePurchase = async (purchaseId: number, asin: string, cost: number) => {
    if (!confirm(canMoney ? `Ejecutar compra real en Amazon para ${asin} por $${cost.toFixed(2)}?` : `¿Ejecutar la compra real en Amazon de ${asin}?`)) return;
    try {
      const res = await api.post(`/amazon/purchases/${purchaseId}/approve?seller_id=${sellerId}`, {});
      if (res.success) { loadData(); loadPOs(); }
      else alert((res as any).error || 'Error approving purchase');
    } catch (e: any) { alert(e.message || 'Error'); }
  };

  const retryPurchase = async (purchaseId: number) => {
    try {
      const res = await api.post(`/amazon/purchases/${purchaseId}/retry?seller_id=${sellerId}`, {});
      if (res.success) loadData();
      else alert((res as any).error || 'Error retrying purchase');
    } catch (e: any) { alert(e.message || 'Error'); }
  };

  const editCost = async (purchaseId: number, newCost: number) => {
    try {
      const res = await api.put(`/amazon/purchases/${purchaseId}/cost`, { unit_cost: newCost });
      if (res.success) { loadData(); if (selectedItem?.id === purchaseId) setSelectedItem({ ...selectedItem, ...(res.data as any) }); }
      else alert((res as any).error || 'Error updating cost');
    } catch (e: any) { alert(e.message || 'Error'); }
  };

  const changeAsin = async (purchaseId: number, newAsin: string) => {
    try {
      const res = await api.put(`/amazon/purchases/${purchaseId}/change-asin`, { asin: newAsin.trim().toUpperCase() });
      if (res.success) { loadData(); setSelectedItem(null); }
      else alert((res as any).error || 'Error changing ASIN');
    } catch (e: any) { alert(e.message || 'Error'); }
  };
  const removeItemFromPO = async (purchaseId: number) => {
    if (!activePO || !confirm('Quitar item del PO?')) return;
    await api.delete(`/amazon/po/${activePO.id}/items/${purchaseId}`);
    openPODetail(activePO); loadPOs();
  };

  // SSE for PO progress
  useEffect(() => {
    const onProgress = (data: any) => {
      if (activePO && data.po_id === activePO.id) {
        setPOProgress(prev => {
          const results = prev ? [...prev.results] : [];
          results.push({ asin: data.asin, success: data.success, error: data.error });
          return { current: (data.index || 0) + 1, total: data.total, results };
        });
      }
    };
    const onCompleted = (data: any) => {
      loadPOs(); loadData();
      if (activePO && data.po_id === activePO.id) openPODetail(activePO);
    };
    const unsubs = [
      subscribe('po:progress', onProgress),
      subscribe('po:completed', onCompleted),
      subscribe('amazon:po-created', () => { loadPOs(); loadData(); }),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe, activePO, sellerId]);

  const statuses = ['all', 'pending_approval', 'ordered', 'shipped', 'delivered', 'delivered_ml', 'cancelled', 'needs_review', 'error', 'unlinked'];
  const activePOs = poList.filter((p: any) => !['completed', 'cancelled'].includes(p.status));
  const pendingCount = data.filter(r => ['pending_approval', 'safeguard_hold', 'needs_review'].includes(r.amazon_status) && !r.po_id).length;
  const fmt = (n: number) => `$${Number(n || 0).toFixed(2)}`;

  return (
    <div className="space-y-4">
      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <StatCard label={t('amz.pending')} value={stats.pending} icon={<Clock size={16} />} color="yellow" />
          <StatCard label={t('amz.ordered')} value={stats.ordered} icon={<Package size={16} />} color="blue" />
          <StatCard label={t('amz.shipped')} value={stats.shipped} icon={<Truck size={16} />} color="cyan" />
          <StatCard label={t('amz.delivered')} value={parseInt(stats.delivered) + parseInt(stats.delivered_ml || 0)} icon={<CheckCircle2 size={16} />} color="green" />
          <StatCard label={t('amz.cancelled')} value={stats.cancelled} icon={<XCircle size={16} />} color="red" />
          {canMoney && <StatCard label={t('amz.totalSpent')} value={`$${Number(stats.total_spent).toLocaleString()}`} icon={<DollarSign size={16} />} color="purple" />}
          {canMoney && <StatCard label={t('amz.avgMargin')} value={`${stats.avg_margin}%`} icon={<TrendingUp size={16} />} color="emerald" />}
        </div>
      )}

      {/* Pending alert */}
      {pendingCount > 0 && (
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 flex items-center justify-between">
          <span className="text-sm text-amber-500">{pendingCount} purchase{pendingCount > 1 ? 's' : ''} pending approval</span>
          <button onClick={() => { setStatusFilter('pending_approval'); pag.resetPage(); }}
            className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-medium">Show Pending</button>
        </div>
      )}

      {/* PO List (collapsible) */}
      {poList.length > 0 && (
        <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)]">
          <button onClick={() => setPOListOpen(!poListOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)] rounded-lg">
            <span>Purchase Orders ({activePOs.length} active / {poList.length} total)</span>
            <span className="text-[var(--color-text-tertiary)]">{poListOpen ? '▲' : '▼'}</span>
          </button>
          {poListOpen && (
            <div className="px-4 pb-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-[var(--color-text-tertiary)] uppercase tracking-wider border-b border-[var(--color-border)]">
                  <th className="p-2 text-left">PO#</th><th className="p-2 text-center">Items</th>
                  <th className="p-2 text-right">Cost</th><th className="p-2 text-right">Profit</th>
                  <th className="p-2 text-right">Margin</th><th className="p-2 text-left">Status</th>
                  <th className="p-2 text-center">Actions</th>
                </tr></thead>
                <tbody>{poList.map((po: any) => (
                  <tr key={po.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer" onClick={() => openPODetail(po)}>
                    <td className="p-2 font-mono text-xs text-[var(--color-primary)]">{po.po_number}</td>
                    <td className="p-2 text-center text-xs">{po.item_count}{po.items_ordered > 0 ? <span className="text-green-500 ml-1">({po.items_ordered})</span> : ''}</td>
                    <td className="p-2 font-mono text-xs text-right text-red-500">{canMoney ? fmt(po.total_cost) : '\u2014'}</td>
                    <td className={`p-2 font-mono text-xs text-right ${parseFloat(po.total_profit) >= 0 ? 'text-green-500' : 'text-red-500'}`}>{canMoney ? fmt(po.total_profit) : '\u2014'}</td>
                    <td className="p-2 font-mono text-xs text-right">{canMoney ? `${parseFloat(po.avg_margin || 0).toFixed(1)}%` : '\u2014'}</td>
                    <td className="p-2"><StatusBadge status={AMZ_STATUS_MAP[po.status] || 'info'} label={po.status} /></td>
                    <td className="p-2 text-center" onClick={e => e.stopPropagation()}>
                      {po.status === 'draft' && can('amazon.delete_po') && <button onClick={() => cancelPO(po.id)} className="text-xs text-red-400 hover:text-red-300">Cancel</button>}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* PO Detail SlidePanel */}
      {activePO && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => { setActivePO(null); setPOProgress(null); }}>
          <div className="w-full max-w-xl bg-[var(--color-bg)] h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-[var(--color-bg)] border-b border-[var(--color-border)] px-4 py-3 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <h3 className="font-bold text-[var(--color-text)]">PO {activePO.po_number}</h3>
                <StatusBadge status={AMZ_STATUS_MAP[activePO.status] || 'info'} label={activePO.status} />
              </div>
              <button onClick={() => { setActivePO(null); setPOProgress(null); }} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]"><X size={18} /></button>
            </div>

            <div className="p-4 space-y-4">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3"><p className="text-[10px] text-[var(--color-text-tertiary)]">Items</p><p className="text-lg font-bold text-[var(--color-primary)]">{activePO.item_count}</p></div>
                {canMoney && <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3"><p className="text-[10px] text-[var(--color-text-tertiary)]">Cost</p><p className="text-lg font-bold text-red-500">{fmt(activePO.total_cost)}</p></div>}
                {canMoney && <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3"><p className="text-[10px] text-[var(--color-text-tertiary)]">Revenue</p><p className="text-lg font-bold text-green-500">{fmt(activePO.total_revenue)}</p></div>}
                {canMoney && <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3"><p className="text-[10px] text-[var(--color-text-tertiary)]">Profit</p><p className={`text-lg font-bold ${parseFloat(activePO.total_profit) >= 0 ? 'text-green-500' : 'text-red-500'}`}>{fmt(activePO.total_profit)}</p></div>}
                {canMoney && <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3"><p className="text-[10px] text-[var(--color-text-tertiary)]">Margin</p><p className="text-lg font-bold text-purple-500">{parseFloat(activePO.avg_margin || 0).toFixed(1)}%</p></div>}
              </div>

              {/* Progress bar */}
              {poProgress && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-[var(--color-text-tertiary)]"><span>Processing...</span><span>{poProgress.current}/{poProgress.total}</span></div>
                  <div className="w-full bg-[var(--color-bg-tertiary)] rounded-full h-2">
                    <div className="h-2 rounded-full transition-all" style={{ width: `${poProgress.total > 0 ? (poProgress.current / poProgress.total) * 100 : 0}%`, backgroundColor: poProgress.results.some(r => !r.success) ? '#f97316' : '#10b981' }} />
                  </div>
                  {poProgress.results.length > 0 && (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {poProgress.results.map((r: any, i: number) => (
                        <div key={i} className="flex items-center gap-2 text-xs">
                          <span className={r.success ? 'text-green-500' : 'text-red-500'}>{r.success ? '✓' : '✗'}</span>
                          <span className="font-mono text-[var(--color-primary)]">{r.asin}</span>
                          {r.error ? <span className="text-red-400 truncate">{r.error}</span> : <span className="text-green-400">ordered</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Items table */}
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-[var(--color-text-tertiary)] uppercase border-b border-[var(--color-border)]">
                  <th className="p-2 text-left">ASIN</th><th className="p-2 text-center">Qty</th>
                  <th className="p-2 text-right">Cost</th><th className="p-2 text-right">Sale</th>
                  <th className="p-2 text-right">Profit</th><th className="p-2 text-left">Delivery</th>
                  <th className="p-2 text-left">Status</th>
                  {activePO.status === 'draft' && <th className="p-2 w-8"></th>}
                </tr></thead>
                <tbody>{poItems.map((item: any) => (
                  <tr key={item.id} className="border-b border-[var(--color-border)]">
                    <td className="p-2 font-mono text-xs text-[var(--color-primary)]">{item.asin}</td>
                    <td className="p-2 text-center text-xs">{item.quantity}</td>
                    <td className="p-2 font-mono text-xs text-right text-red-500">{canMoney ? fmt(item.total_cost) : '\u2014'}</td>
                    <td className="p-2 font-mono text-xs text-right text-green-500">{canMoney ? fmt(item.sale_amount_usd) : '\u2014'}</td>
                    <td className={`p-2 font-mono text-xs text-right ${parseFloat(item.gross_profit || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>{canMoney ? fmt(item.gross_profit) : '\u2014'}</td>
                    <td className="p-2 text-xs text-[var(--color-text-tertiary)] max-w-[140px] truncate" title={item.delivery_info?.text || item.etd || ''}>
                      {item.delivery_info?.text || (item.etd ? new Date(item.etd).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—')}
                    </td>
                    <td className="p-2"><StatusBadge status={AMZ_STATUS_MAP[item.amazon_status] || 'unknown'} label={item.amazon_status?.replace(/_/g, ' ')} />
                      {item.amazon_status === 'safeguard_hold' && <span className="ml-1 text-amber-400 text-[10px]">⚠</span>}
                    </td>
                    {activePO.status === 'draft' && (
                      <td className="p-2 text-center">{can('amazon.delete_po') && <button onClick={() => removeItemFromPO(item.id)} className="text-red-400 hover:text-red-300 text-xs">✕</button>}</td>
                    )}
                  </tr>
                ))}</tbody>
              </table>

              {/* Action buttons */}
              <div className="flex gap-2 pt-2 border-t border-[var(--color-border)]">
                {activePO.status === 'draft' && <>
                  <Can cap="amazon.approve"><button onClick={approvePO} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium">Approve & Buy All</button></Can>
                  <Can cap="amazon.delete_po"><button onClick={() => cancelPO(activePO.id)} className="px-4 py-2 border border-red-500 text-red-500 rounded-lg text-sm hover:bg-red-950/30">Cancel PO</button></Can>
                </>}
                {activePO.status === 'processing' && <>
                  <span className="px-4 py-2 text-sm text-blue-400 font-medium">Processing...</span>
                  <Can cap="amazon.operate"><button onClick={stopPO} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm">Stop</button></Can>
                </>}
                {['partial', 'stopped'].includes(activePO.status) && <>
                  <Can cap="amazon.operate"><button onClick={retryPO} className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium">Retry Failed</button></Can>
                  <Can cap="amazon.operate"><button onClick={releasePO} className="px-4 py-2 border border-[var(--color-border)] text-[var(--color-text-secondary)] rounded-lg text-sm hover:bg-[var(--color-bg-tertiary)]">Release Failed</button></Can>
                </>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters + Create PO */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[200px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); pag.resetPage(); }}
            placeholder={`${t('common.search')} ASIN, Order ID...`}
            className="w-full pl-9 pr-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          {statuses.map(s => (
            <option key={s} value={s}>{s === 'all' ? t('amz.allStatuses') : s.replace(/_/g, ' ')}</option>
          ))}
        </select>
        {selected.size > 0 && canOperate && (
          <>
            {/* Crear una OC fija costos: pide operar Y ver dinero (igual que Stock PO). */}
            {canMoney && <button onClick={createPO}
              className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium">
              Create PO ({selected.size})
            </button>}
            {/* Crear un Stock PO fija costos: pide operar Y ver dinero. */}
            {canMoney && <button onClick={() => setStockPOModalOpen(true)}
              className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium">
              Stock PO ({selected.size})
            </button>}
            <button onClick={() => setSelected(new Set())} className="text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)]">✕</button>
          </>
        )}
        <div className="ml-auto flex gap-2">
          {/* La Compra Manual escribe costo unitario y envío: pide operar Y ver dinero. */}
          {canOperate && canMoney && <button onClick={() => setGenericModalOpen(true)}
            className="px-3 py-2 border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:border-[var(--color-primary)] rounded-lg text-sm font-medium transition-colors">
            + Compra Manual
          </button>}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm table-fixed">
          <thead>
            <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-tertiary)]">
              <th className="w-[40px] px-3 py-3">
                {canOperate && <input type="checkbox" checked={data.length > 0 && selected.size === data.length} onChange={toggleAll}
                  className="w-4 h-4 rounded border-[var(--color-border)] accent-[var(--color-primary)] cursor-pointer" />}
              </th>
              <th className="text-left py-3 pr-2 text-xs text-[var(--color-text-tertiary)] font-medium uppercase tracking-wider w-[32%]">Producto</th>
              <th className="text-center px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase tracking-wider w-[4%]">Qty</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase tracking-wider w-[10%]">Orden</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase tracking-wider w-[10%]">{t('amz.status')}</th>
              <th className="text-right px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase tracking-wider w-[7%]">Costo</th>
              <th className="text-right px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase tracking-wider w-[7%]">Neto</th>
              <th className="text-right px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase tracking-wider w-[6%]">Profit</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase tracking-wider w-[10%]">Tracking</th>
              <th className="text-left px-2 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase tracking-wider w-[8%]">Delivery</th>
              <th className="text-left px-2 pr-3 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase tracking-wider w-[6%]">Fecha</th>
              <th className="text-right px-2 pr-3 py-3 text-xs text-[var(--color-text-tertiary)] font-medium uppercase tracking-wider w-[9%]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="text-center py-16">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--color-primary)]" />
              </td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={12} className="text-center py-16 text-[var(--color-text-tertiary)]">{t('common.noData')}</td></tr>
            ) : data.map((row) => (
              <tr
                key={row.id}
                onClick={() => setSelectedItem(row)}
                className={`border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer transition-colors ${selected.has(row.id) ? 'bg-[var(--color-primary)]/5' : row.amazon_status === 'needs_review' ? 'bg-orange-50 dark:bg-orange-950/25' : ''}`}
              >
                {/* Checkbox */}
                <td className="px-3 py-4">
                  {canOperate && <input type="checkbox" checked={selected.has(row.id)} onChange={() => {}}
                    onClick={(e) => toggleSelect(row.id, e)}
                    className="w-4 h-4 rounded border-[var(--color-border)] accent-[var(--color-primary)] cursor-pointer" />}
                </td>
                {/* Producto: foto + titulo + ASIN */}
                <td className="pr-2 py-4">
                  <div className="flex items-center gap-3">
                    {row.product_image ? (
                      <img src={row.product_image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0 border border-[var(--color-border)]" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-[var(--color-bg-tertiary)] shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-[var(--color-text)] font-medium leading-tight line-clamp-2 text-[13px]">
                        {row.product_title || 'Sin titulo'}
                      </p>
                      <p className="text-[11px] text-[var(--color-text-tertiary)] mt-0.5 font-mono">{row.asin}</p>
                    </div>
                  </div>
                </td>
                {/* Qty */}
                <td className="px-2 py-4 text-center text-[13px] text-[var(--color-text)]">
                  {row.quantity > 1 ? <span className="font-bold">{row.quantity}</span> : row.quantity || 1}
                </td>
                {/* Orden ML */}
                <td className="px-2 py-4">
                  <EntityLink type="ml_order" value={String(row.ml_order_id)} />
                </td>
                {/* Status */}
                <td className="px-2 py-4">
                  <StatusBadge status={AMZ_STATUS_MAP[row.amazon_status] || 'unknown'} label={row.amazon_status?.replace(/_/g, ' ')} />
                </td>
                {/* Costo total (unit_cost × qty) */}
                <td className="px-2 py-4 text-right font-mono text-[13px] text-[var(--color-text)]">
                  {!canMoney ? '—' : row.total_cost ? `$${Number(row.total_cost).toFixed(2)}` : row.unit_cost ? `$${Number(row.unit_cost).toFixed(2)}` : '—'}
                </td>
                {/* Neto real (sale_price - total_fees - shipping) */}
                <td className="px-2 py-4 text-right font-mono text-[13px] text-[var(--color-text)]">
                  {(() => {
                    const price = Number(row.sale_price || 0);
                    const fee = Number(row.real_total_fee ?? row.real_sale_fee ?? row.sale_fee ?? 0);
                    const ship = Number(row.real_shipping_cost ?? row.ml_shipping_cost ?? 0);
                    const net = price - fee - ship;
                    return canMoney && price > 0 ? `$${net.toFixed(2)}` : '—';
                  })()}
                </td>
                {/* Profit $ */}
                <td className="px-2 py-4 text-right">
                  {(() => {
                    const price = Number(row.sale_price || 0);
                    const fee = Number(row.real_total_fee ?? row.real_sale_fee ?? row.sale_fee ?? 0);
                    const ship = Number(row.real_shipping_cost ?? row.ml_shipping_cost ?? 0);
                    const cost = Number(row.total_cost || row.unit_cost || 0);
                    const profit = price - fee - ship - cost;
                    return canMoney && price > 0 && cost > 0 ? (
                      <span className={`font-mono font-bold text-[13px] ${profit >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {profit >= 0 ? '+' : ''}${profit.toFixed(2)}
                      </span>
                    ) : '—';
                  })()}
                </td>
                {/* Tracking */}
                <td className="px-2 py-4 text-[13px]">
                  {row.tracking_number ? (
                    <EntityLink type="tracking" value={row.tracking_number} />
                  ) : (
                    <span className="text-[var(--color-text-tertiary)]">—</span>
                  )}
                </td>
                {/* Delivery */}
                <td className="px-2 py-4 text-xs whitespace-nowrap">
                  {row.actual_delivery_date ? (
                    <span className="text-green-500 font-medium" title="Entregado">
                      {new Date(row.actual_delivery_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  ) : row.estimated_delivery_end ? (
                    <span className={`font-medium ${new Date(row.estimated_delivery_end) < new Date() ? 'text-red-500' : 'text-[var(--color-text)]'}`} title="ETD post-compra">
                      {new Date(row.estimated_delivery_end).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  ) : row.etd ? (
                    <span className="text-blue-500 font-medium" title="ETD Amazon">
                      {new Date(row.etd).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  ) : (
                    <span className="text-[var(--color-text-tertiary)]">—</span>
                  )}
                </td>
                {/* Fecha */}
                <td className="px-2 py-4 text-[var(--color-text-secondary)] text-xs whitespace-nowrap">
                  {row.created_at ? new Date(row.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}
                </td>
                {/* Actions */}
                <td className="px-2 pr-3 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    {can('amazon.approve') && ['pending_approval', 'safeguard_hold', 'cancelled', 'needs_review'].includes(row.amazon_status) && (
                      <button
                        onClick={() => approvePurchase(row.id, row.asin, Number(row.unit_cost || 0))}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-white rounded text-xs font-medium transition-colors ${
                          row.amazon_status === 'cancelled' || row.amazon_status === 'needs_review' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
                        }`}
                        title={row.amazon_status === 'cancelled' ? 'Re-comprar en Amazon' : row.amazon_status === 'needs_review' ? 'Re-comprar (delivery lento detectado)' : 'Aprobar y comprar en Amazon'}
                      >
                        <Check size={12} /> {row.amazon_status === 'cancelled' || row.amazon_status === 'needs_review' ? 'Re-buy' : 'Approve'}
                      </button>
                    )}
                    {can('amazon.approve') && row.amazon_status === 'error' && (
                      <button
                        onClick={() => retryPurchase(row.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs font-medium transition-colors"
                        title="Reintentar compra"
                      >
                        <RotateCcw size={12} /> Retry
                      </button>
                    )}
                    {canOperate && !row.amazon_order_id && row.ml_order_id && !['cancelled', 'linked_manual'].includes(row.amazon_status) && (
                      <button
                        onClick={() => setLinkModalOrder(row.ml_order_id)}
                        className="inline-flex items-center gap-1 px-2 py-1 border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white rounded text-xs font-medium transition-colors"
                        title="Vincular a compra Amazon"
                      >
                        <Link2 size={12} /> Link
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {/* Detail Modal */}
      {selectedItem && (
        <PurchaseDetailModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onApprove={approvePurchase}
          onRetry={retryPurchase}
          onEditCost={editCost}
          onChangeAsin={changeAsin}
        />
      )}

      {/* Link Purchase Modal */}
      {linkModalOrder && (
        <LinkPurchaseModal
          sellerId={sellerId}
          mlOrderId={linkModalOrder}
          onClose={() => setLinkModalOrder(null)}
          onLinked={loadData}
        />
      )}

      {/* Generic Purchase Modal */}
      {genericModalOpen && (
        <GenericPurchaseModal
          sellerId={sellerId}
          onClose={() => setGenericModalOpen(false)}
          onCreated={loadData}
        />
      )}

      {/* Stock PO Modal */}
      {stockPOModalOpen && selected.size > 0 && (
        <StockPOModal
          sellerId={sellerId}
          purchases={data.filter((d: any) => selected.has(d.id))}
          onClose={() => setStockPOModalOpen(false)}
          onCreated={() => { loadData(); loadPOs(); setSelected(new Set()); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────
// PURCHASE ORDERS TAB
// ─────────────────────────────────────────────────

function PurchaseOrdersTab() {
  const { t } = useTranslation();
  const can = useCan();
  const canMoney = can('global.view_money');
  const { sellerId } = useUIStore();
  const { subscribe } = useSSEStore();
  const [pos, setPOs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activePO, setActivePO] = useState<any>(null);
  const [poItems, setPOItems] = useState<any[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [editAsin, setEditAsin] = useState<{ purchaseId: number; asin: string } | null>(null);
  const [newAsin, setNewAsin] = useState('');

  const loadPOs = useCallback(async () => {
    try {
      const res = await api.get<any[]>(`/amazon/po?seller_id=${sellerId}&limit=50`);
      if (res.success && res.data) setPOs(res.data);
    } catch {} finally { setLoading(false); }
  }, [sellerId]);

  useEffect(() => { loadPOs(); }, [loadPOs]);

  const loadRef = useRef(loadPOs);
  loadRef.current = loadPOs;
  useEffect(() => {
    const refresh = () => loadRef.current();
    const unsubs = [subscribe('po:completed', refresh), subscribe('amazon:po-created', refresh)];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  const openPO = async (po: any) => {
    setActivePO(po);
    const res = await api.get<any>(`/amazon/po/${po.id}?seller_id=${sellerId}`);
    if (res.success && res.data) {
      setActivePO(res.data);
      setPOItems(res.data.items || []);
    }
  };

  const approvePO = async () => {
    if (!activePO) return;
    await api.post(`/amazon/po/${activePO.id}/approve?seller_id=${sellerId}`, {});
    loadPOs();
  };

  const stopPO = async () => {
    if (!activePO) return;
    await api.post(`/amazon/po/${activePO.id}/stop`, {});
  };

  const retryPO = async () => {
    if (!activePO) return;
    await api.post(`/amazon/po/${activePO.id}/retry?seller_id=${sellerId}`, {});
    loadPOs();
  };

  const releasePO = async () => {
    if (!activePO) return;
    await api.post(`/amazon/po/${activePO.id}/release`, {});
    openPO(activePO);
    loadPOs();
  };

  const cancelPO = async (poId: number) => {
    await api.delete(`/amazon/po/${poId}`);
    setActivePO(null);
    loadPOs();
  };

  const refreshPrices = async () => {
    if (!activePO) return;
    setRefreshing(true);
    await api.post(`/amazon/po/${activePO.id}/refresh`, {});
    await openPO(activePO);
    setRefreshing(false);
  };

  const removeItem = async (purchaseId: number) => {
    if (!activePO) return;
    await api.delete(`/amazon/po/${activePO.id}/items/${purchaseId}`);
    openPO(activePO);
    loadPOs();
  };

  const changeAsin = async () => {
    if (!editAsin) return;
    const res = await api.put(`/amazon/purchases/${editAsin.purchaseId}/change-asin`, { asin: newAsin });
    if (res.success) {
      setEditAsin(null);
      setNewAsin('');
      openPO(activePO);
    }
  };

  const fmt = (n: number) => `$${Number(n || 0).toFixed(2)}`;
  const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  const STATUS_COLORS: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    partial: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    stopped: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" /></div>;

  return (
    <div className="space-y-4">
      {/* PO List */}
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
          <h3 className="text-sm font-medium text-[var(--color-text)]">Purchase Orders</h3>
          <span className="text-xs text-[var(--color-text-tertiary)]">{pos.length} total</span>
        </div>
        {pos.length === 0 ? (
          <p className="text-center py-8 text-[var(--color-text-tertiary)]">No purchase orders yet. Select purchases and click "Crear PO".</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-tertiary)]">
                <th className="text-left px-4 py-2 text-xs text-[var(--color-text-tertiary)] font-medium">PO#</th>
                <th className="text-center px-2 py-2 text-xs text-[var(--color-text-tertiary)] font-medium">Items</th>
                <th className="text-right px-2 py-2 text-xs text-[var(--color-text-tertiary)] font-medium">Costo</th>
                <th className="text-right px-2 py-2 text-xs text-[var(--color-text-tertiary)] font-medium">Profit</th>
                <th className="text-center px-2 py-2 text-xs text-[var(--color-text-tertiary)] font-medium">Status</th>
                <th className="text-left px-2 py-2 text-xs text-[var(--color-text-tertiary)] font-medium">Fecha</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {pos.map((po: any) => (
                <tr key={po.id} onClick={() => openPO(po)} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)] cursor-pointer">
                  <td className="px-4 py-3 font-mono text-[var(--color-primary)] font-medium">{po.po_number}</td>
                  <td className="px-2 py-3 text-center">{po.item_count}{po.items_ordered > 0 ? <span className="text-green-500 ml-1">({po.items_ordered})</span> : ''}</td>
                  <td className="px-2 py-3 text-right font-mono">{canMoney ? fmt(po.total_cost) : '\u2014'}</td>
                  <td className={`px-2 py-3 text-right font-mono font-bold ${parseFloat(po.total_profit) >= 0 ? 'text-green-500' : 'text-red-500'}`}>{canMoney ? fmt(po.total_profit) : '\u2014'}</td>
                  <td className="px-2 py-3 text-center"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[po.status] || ''}`}>{po.status}</span></td>
                  <td className="px-2 py-3 text-xs text-[var(--color-text-secondary)]">{fmtDate(po.created_at)}</td>
                  <td className="px-3 py-3">{po.status === 'draft' && can('amazon.delete_po') && <button onClick={(e) => { e.stopPropagation(); cancelPO(po.id); }} className="text-xs text-red-400 hover:text-red-600">Cancel</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* PO Detail Panel */}
      {activePO && (
        <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-sm font-bold text-[var(--color-text)]">{activePO.po_number}</h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[activePO.status] || ''}`}>{activePO.status}</span>
            </div>
            <button onClick={() => setActivePO(null)} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]"><X size={16} /></button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4">
            <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3">
              <p className="text-[10px] text-[var(--color-text-tertiary)]">Items</p>
              <p className="text-lg font-bold text-[var(--color-primary)]">{activePO.item_count}</p>
            </div>
            {canMoney && <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3">
              <p className="text-[10px] text-[var(--color-text-tertiary)]">Costo Total</p>
              <p className="text-lg font-bold text-red-500">{fmt(activePO.total_cost)}</p>
            </div>}
            {canMoney && <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3">
              <p className="text-[10px] text-[var(--color-text-tertiary)]">Revenue</p>
              <p className="text-lg font-bold text-[var(--color-text)]">{fmt(activePO.total_revenue)}</p>
            </div>}
            {canMoney && <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3">
              <p className="text-[10px] text-[var(--color-text-tertiary)]">Profit</p>
              <p className={`text-lg font-bold ${parseFloat(activePO.total_profit) >= 0 ? 'text-green-500' : 'text-red-500'}`}>{fmt(activePO.total_profit)}</p>
            </div>}
            {canMoney && <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3">
              <p className="text-[10px] text-[var(--color-text-tertiary)]">Margen</p>
              <p className="text-lg font-bold text-purple-500">{parseFloat(activePO.avg_margin || 0).toFixed(1)}%</p>
            </div>}
          </div>

          {/* Items table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-[var(--color-border)] bg-[var(--color-bg-tertiary)]">
                  <th className="text-left px-4 py-2 text-xs text-[var(--color-text-tertiary)]">Producto</th>
                  <th className="text-center px-2 py-2 text-xs text-[var(--color-text-tertiary)]">Qty</th>
                  <th className="text-right px-2 py-2 text-xs text-[var(--color-text-tertiary)]">Costo</th>
                  <th className="text-right px-2 py-2 text-xs text-[var(--color-text-tertiary)]">Neto</th>
                  <th className="text-right px-2 py-2 text-xs text-[var(--color-text-tertiary)]">Profit</th>
                  <th className="text-left px-2 py-2 text-xs text-[var(--color-text-tertiary)]">Delivery</th>
                  <th className="text-center px-2 py-2 text-xs text-[var(--color-text-tertiary)]">Status</th>
                  {activePO.status === 'draft' && <th className="px-2 py-2"></th>}
                </tr>
              </thead>
              <tbody>
                {poItems.map((item: any) => {
                  const net = parseFloat(item.sale_amount_usd || 0) - parseFloat(item.sale_fee || 0) - parseFloat(item.ml_shipping_cost || 0);
                  const profit = net - parseFloat(item.total_cost || 0);
                  return (
                    <tr key={item.id} className={`border-b border-[var(--color-border)] ${item.amazon_status === 'safeguard_hold' ? 'bg-yellow-50/50 dark:bg-yellow-950/20' : item.amazon_status === 'needs_review' ? 'bg-orange-50 dark:bg-orange-950/25' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {item.product_image && <img src={item.product_image} alt="" className="w-10 h-10 rounded object-cover" />}
                          <div>
                            <p className="text-[var(--color-text)] text-[13px] line-clamp-1">{item.product_title || item.asin}</p>
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] text-[var(--color-text-tertiary)] font-mono">{item.asin}</span>
                              {activePO.status === 'draft' && can('amazon.edit_cost') && (
                                <button onClick={() => { setEditAsin({ purchaseId: item.id, asin: item.asin }); setNewAsin(''); }}
                                  className="text-[10px] text-blue-400 hover:text-blue-600">cambiar</button>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-3 text-center text-[13px]">{item.quantity > 1 ? <span className="font-bold">{item.quantity}</span> : item.quantity || 1}</td>
                      <td className="px-2 py-3 text-right font-mono text-[13px]">{canMoney ? fmt(item.total_cost) : '—'}</td>
                      <td className="px-2 py-3 text-right font-mono text-[13px]">{canMoney && net > 0 ? fmt(net) : '—'}</td>
                      <td className={`px-2 py-3 text-right font-mono font-bold text-[13px] ${profit >= 0 ? 'text-green-500' : 'text-red-500'}`}>{canMoney && net > 0 ? `${profit >= 0 ? '+' : ''}${fmt(profit)}` : '—'}</td>
                      <td className="px-2 py-3 text-xs">{item.etd ? <span className="text-blue-500">{new Date(item.etd).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span> : '—'}</td>
                      <td className="px-2 py-3 text-center"><StatusBadge status={AMZ_STATUS_MAP[item.amazon_status] || 'unknown'} label={item.amazon_status?.replace(/_/g, ' ')} /></td>
                      {activePO.status === 'draft' && (
                        <td className="px-2 py-3">{can('amazon.delete_po') && <button onClick={() => removeItem(item.id)} className="text-red-400 hover:text-red-600 text-xs">×</button>}</td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Change ASIN modal */}
          {editAsin && (
            <div className="px-4 py-3 border-t border-[var(--color-border)] bg-blue-50/50 dark:bg-blue-950/20 flex items-center gap-3">
              <span className="text-xs text-[var(--color-text-secondary)]">Cambiar ASIN de {editAsin.asin}:</span>
              <input type="text" value={newAsin} onChange={e => setNewAsin(e.target.value)} placeholder="Nuevo ASIN..."
                className="px-3 py-1.5 border border-[var(--color-border)] rounded text-sm bg-[var(--color-bg)] text-[var(--color-text)] w-40" />
              <button onClick={changeAsin} disabled={!newAsin} className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs disabled:opacity-50">Aplicar</button>
              <button onClick={() => setEditAsin(null)} className="text-xs text-[var(--color-text-secondary)]">Cancelar</button>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex items-center gap-2 px-4 py-3 border-t border-[var(--color-border)]">
            {activePO.status === 'draft' && (
              <>
                <Can cap="amazon.operate"><button onClick={refreshPrices} disabled={refreshing}
                  className="px-4 py-2 border border-[var(--color-border)] text-[var(--color-text-secondary)] rounded-lg text-sm hover:bg-[var(--color-bg-tertiary)] flex items-center gap-1.5 disabled:opacity-50">
                  {refreshing ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                  Refresh Precios
                </button></Can>
                <Can cap="amazon.approve"><button onClick={approvePO}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-1.5">
                  <Play size={14} /> Aprobar ({activePO.item_count} items)
                </button></Can>
                <Can cap="amazon.delete_po"><button onClick={() => cancelPO(activePO.id)}
                  className="px-4 py-2 border border-red-500 text-red-500 rounded-lg text-sm hover:bg-red-50 dark:hover:bg-red-950/30">
                  Cancelar PO
                </button></Can>
              </>
            )}
            {activePO.status === 'processing' && can('amazon.operate') && (
              <button onClick={stopPO}
                className="px-4 py-2 border border-orange-500 text-orange-500 rounded-lg text-sm hover:bg-orange-50 dark:hover:bg-orange-950/30 flex items-center gap-1.5">
                <Pause size={14} /> Detener
              </button>
            )}
            {['partial', 'stopped'].includes(activePO.status) && can('amazon.operate') && (
              <>
                <button onClick={retryPO}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm flex items-center gap-1.5">
                  <Zap size={14} /> Reintentar
                </button>
                <button onClick={releasePO}
                  className="px-4 py-2 border border-purple-500 text-purple-500 rounded-lg text-sm hover:bg-purple-50 dark:hover:bg-purple-950/30">
                  Liberar fallidos
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────
// PRICE MONITOR TAB
// ─────────────────────────────────────────────────

function formatPMReason(reason: string, action?: string): string {
  // delivery:17d/6d → "Delivery 17d (max 6d)"
  const dm = reason.match(/^delivery:(\d+)d\/(\d+)d$/);
  if (dm) return `Delivery ${dm[1]}d (max ${dm[2]}d)`;
  // restocked with previousPause info
  if (action === 'restocked' && reason) return `Reactivado (prev: ${reason})`;
  return reason;
}

function PriceMonitorTab() {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const { sellerId } = useUIStore();
  const [config, setConfig] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const pag = usePagination('amazon-pm');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        seller_id: String(sellerId),
        page: String(pag.page),
        per_page: String(pag.perPage),
      });
      if (actionFilter !== 'all') params.set('action', actionFilter);

      const [histRes, statsRes] = await Promise.all([
        api.get<any[]>(`/amazon/price-monitor/history?${params}`),
        api.get<any>(`/amazon/price-monitor/stats?seller_id=${sellerId}`),
      ]);

      if (histRes.success && histRes.data) {
        setHistory(histRes.data);
        pag.setTotal((histRes as any).meta?.total || 0);
      }
      if (statsRes.success && statsRes.data) {
        setConfig(statsRes.data.config);
        setStats(statsRes.data.actions_24h);
      }
    } catch (err) {
      console.error('Failed to load PM data:', err);
    } finally {
      setLoading(false);
    }
  }, [sellerId, pag.page, pag.perPage, actionFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const actions = ['all', 'repriced', 'paused', 'reactivated', 'skipped'];

  return (
    <div className="space-y-4">
      {/* PM Config Card */}
      {config && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-[var(--color-text)] flex items-center gap-2">
                <Activity size={16} className="text-[var(--color-primary)]" />
                {t('amz.pmConfig')}
              </h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                {config.enabled ? t('amz.pmEnabled') : t('amz.pmDisabled')}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-[var(--color-text-tertiary)]">{t('amz.pmInterval')}</span>
                <div className="text-[var(--color-text)] font-medium">{config.check_interval_hours}h</div>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">{t('amz.pmThreshold')}</span>
                <div className="text-[var(--color-text)] font-medium">{canMoney ? `$${config.price_threshold_usd}` : '\u2014'}</div>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">{t('amz.pmAutoStock')}</span>
                <div className="text-[var(--color-text)] font-medium">{config.auto_pause_no_stock ? '✓' : '✗'}</div>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">{t('amz.pmAutoReactivate')}</span>
                <div className="text-[var(--color-text)] font-medium">{config.auto_reactivate_on_restock ? '✓' : '✗'}</div>
              </div>
            </div>
            {/* Offer selection badges */}
            <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-[var(--color-border)]">
              {config.require_prime && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">PRIME</span>
              )}
              {config.prefer_fba && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">FBA</span>
              )}
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {(config.offer_condition || 'new').toUpperCase()}
              </span>
              {config.min_delivery_stability > 1 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                  Stability: {config.min_delivery_stability}
                </span>
              )}
              {config.max_delivery_days && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                  Max {config.max_delivery_days}d
                </span>
              )}
            </div>
          </div>

          {/* Last Run Card */}
          <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
            <h3 className="font-medium text-[var(--color-text)] flex items-center gap-2 mb-3">
              <Clock size={16} className="text-[var(--color-primary)]" />
              {t('amz.pmLastRun')}
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-[var(--color-text-tertiary)]">{t('common.date')}</span>
                <div className="text-[var(--color-text)] font-medium">
                  {config.last_run_at ? new Date(config.last_run_at).toLocaleString() : '—'}
                </div>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">{t('amz.pmDuration')}</span>
                <div className="text-[var(--color-text)] font-medium">
                  {config.last_run_duration_ms ? `${Math.round(config.last_run_duration_ms / 1000)}s` : '—'}
                </div>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">{t('amz.pmItems')}</span>
                <div className="text-[var(--color-text)] font-medium">{config.last_run_items?.toLocaleString() || 0}</div>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">{t('amz.pmRepriced')}</span>
                <div className="text-blue-500 font-medium">{config.last_run_repriced?.toLocaleString() || 0}</div>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">{t('amz.pmPaused')}</span>
                <div className="text-red-500 font-medium">{config.last_run_paused?.toLocaleString() || 0}</div>
              </div>
              <div>
                <span className="text-[var(--color-text-tertiary)]">{t('amz.pmErrors')}</span>
                <div className={`font-medium ${config.last_run_errors > 0 ? 'text-red-500' : 'text-green-500'}`}>
                  {config.last_run_errors?.toLocaleString() || 0}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 24h Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label={t('common.total')} value={stats.total_changes} icon={<BarChart3 size={16} />} color="gray" />
          <StatCard label={t('amz.repriced')} value={stats.repriced} icon={<ArrowUpDown size={16} />} color="blue" />
          <StatCard label={t('amz.paused')} value={stats.paused} icon={<Pause size={16} />} color="red" />
          <StatCard label={t('amz.reactivated')} value={stats.reactivated} icon={<Play size={16} />} color="green" />
          <StatCard label={t('amz.skipped')} value={stats.skipped} icon={<XCircle size={16} />} color="gray" />
        </div>
      )}

      {/* Filter + Table */}
      <div className="flex gap-3 items-center">
        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); pag.resetPage(); }}
          className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        >
          {actions.map(a => (
            <option key={a} value={a}>{a === 'all' ? t('amz.allActions') : t(`amz.${a}`)}</option>
          ))}
        </select>
        <span className="text-sm text-[var(--color-text-secondary)]">{t('amz.priceHistory')}</span>
      </div>

      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-border)]">
              <th className="text-left px-4 py-3 text-[var(--color-text-secondary)] font-medium">Producto</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('amz.asin')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('amz.action')}</th>
              <th className="text-right px-3 py-3 text-[var(--color-text-secondary)] font-medium">Amazon {t('amz.oldPrice')}</th>
              <th className="text-right px-3 py-3 text-[var(--color-text-secondary)] font-medium">Amazon {t('amz.newPrice')}</th>
              <th className="text-right px-3 py-3 text-[var(--color-text-secondary)] font-medium">ML {t('amz.oldPrice')}</th>
              <th className="text-right px-3 py-3 text-[var(--color-text-secondary)] font-medium">ML {t('amz.newPrice')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('amz.reason')}</th>
              <th className="text-left px-3 py-3 text-[var(--color-text-secondary)] font-medium">{t('common.date')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="text-center py-12">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-[var(--color-primary)]" />
              </td></tr>
            ) : history.length === 0 ? (
              <tr><td colSpan={12} className="text-center py-12 text-[var(--color-text-tertiary)]">{t('common.noData')}</td></tr>
            ) : history.map((row, idx) => (
              <tr key={idx} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {row.product_image ? (
                      <img src={row.product_image} alt="" className="w-8 h-8 rounded object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded bg-[var(--color-bg-tertiary)] flex items-center justify-center">
                        <Package size={14} className="text-[var(--color-text-tertiary)]" />
                      </div>
                    )}
                    <span className="text-[var(--color-text)] line-clamp-1 max-w-[180px]">
                      {row.product_title || row.asin}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <EntityLink type="asin" value={row.asin} />
                </td>
                <td className="px-3 py-3">
                  <StatusBadge status={PM_ACTION_MAP[row.action] || 'unknown'} label={row.action} />
                </td>
                <td className="px-3 py-3 text-right font-mono text-[var(--color-text-secondary)]">
                  {canMoney && row.old_amazon_price ? `$${Number(row.old_amazon_price).toFixed(2)}` : '—'}
                </td>
                <td className="px-3 py-3 text-right font-mono text-[var(--color-text)]">
                  {canMoney && row.new_amazon_price ? `$${Number(row.new_amazon_price).toFixed(2)}` : '—'}
                </td>
                <td className="px-3 py-3 text-right font-mono text-[var(--color-text-secondary)]">
                  {canMoney && row.old_ml_price ? `$${Number(row.old_ml_price).toFixed(2)}` : '—'}
                </td>
                <td className="px-3 py-3 text-right font-mono text-[var(--color-text)]">
                  {canMoney && row.new_ml_price ? `$${Number(row.new_ml_price).toFixed(2)}` : '—'}
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs max-w-[200px]" title={row.reason}>
                  {row.reason ? formatPMReason(row.reason, row.action) : '—'}
                </td>
                <td className="px-3 py-3 text-[var(--color-text-secondary)] text-xs whitespace-nowrap">
                  {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────
// CATALOG TAB
// ─────────────────────────────────────────────────

function CatalogTab() {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const [data, setData] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const pag = usePagination('amazon-catalog');
  const [selectedProduct, setSelectedProduct] = useState<any>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(pag.page),
        per_page: String(pag.perPage),
      });
      if (search) params.set('search', search);

      const [dataRes, statsRes] = await Promise.all([
        api.get<any[]>(`/amazon/cache?${params}`),
        api.get<any>(`/amazon/cache/stats`),
      ]);
      if (dataRes.success && dataRes.data) {
        setData(dataRes.data);
        pag.setTotal((dataRes as any).meta?.total || 0);
      }
      if (statsRes.success && statsRes.data) setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load catalog:', err);
    } finally {
      setLoading(false);
    }
  }, [pag.page, pag.perPage, search]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="space-y-4">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <StatCard label={t('amz.cacheTotal')} value={Number(stats.total_products).toLocaleString()} icon={<Database size={16} />} color="blue" />
          <StatCard label={t('amz.cacheAvailable')} value={stats.available} icon={<CheckCircle2 size={16} />} color="green" />
          <StatCard label={t('amz.cachePrime')} value={stats.prime} icon={<Zap size={16} />} color="purple" />
          <StatCard label={t('amz.cacheInStock')} value={stats.in_stock} icon={<Package size={16} />} color="emerald" />
          <StatCard label={t('amz.cacheNoOffer')} value={stats.no_offer} icon={<XCircle size={16} />} color="red" />
          <StatCard label={t('amz.cacheFetched24h')} value={stats.fetched_24h} icon={<Clock size={16} />} color="cyan" />
          {canMoney && <StatCard label={t('amz.cacheAvgPrice')} value={`$${stats.avg_price}`} icon={<DollarSign size={16} />} color="yellow" />}
          <StatCard label={t('amz.cacheAvgRating')} value={`${stats.avg_rating}★`} icon={<Star size={16} />} color="orange" />
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-tertiary)]" />
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); pag.resetPage(); }}
          placeholder={`${t('common.search')} ASIN, título, marca...`}
          className="w-full pl-9 pr-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
        />
      </div>

      {/* Product Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--color-primary)]" />
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-12 text-[var(--color-text-tertiary)]">{t('common.noData')}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {data.map((product) => (
            <div
              key={product.asin}
              onClick={() => setSelectedProduct(product)}
              className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4 hover:border-[var(--color-primary)] cursor-pointer transition-colors"
            >
              <div className="flex gap-3">
                {product.main_image ? (
                  <img src={product.main_image} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded bg-[var(--color-bg-tertiary)] flex items-center justify-center flex-shrink-0">
                    <Package size={20} className="text-[var(--color-text-tertiary)]" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <span className="font-mono text-xs text-teal-500 font-medium">{product.asin}</span>
                  <p className="text-sm text-[var(--color-text)] line-clamp-2 mt-0.5">{product.title || '—'}</p>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  {product.brand && (
                    <span className="px-2 py-0.5 bg-[var(--color-bg-tertiary)] rounded text-[var(--color-text-secondary)]">{product.brand}</span>
                  )}
                  {product.amz_is_prime && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded font-medium">Prime</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {product.rating && (
                    <span className="flex items-center gap-1 text-yellow-500">
                      <Star size={12} fill="currentColor" />
                      {Number(product.rating).toFixed(1)}
                    </span>
                  )}
                  {canMoney && (product.price || product.amz_price) && (
                    <span className="font-mono font-medium text-[var(--color-text)]">
                      ${Number(product.amz_price || product.price).toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination
        page={pag.page}
        totalPages={pag.totalPages}
        total={pag.total}
        perPage={pag.perPage}
        onPageChange={pag.setPage}
        onPerPageChange={pag.setPerPage}
      />

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────
// SHARED COMPONENTS
// ─────────────────────────────────────────────────

function StatCard({ label, value, icon, color }: { label: string; value: any; icon: React.ReactNode; color: string }) {
  const colorMap: Record<string, string> = {
    blue: 'text-blue-500 bg-blue-50 dark:bg-blue-900/20',
    green: 'text-green-500 bg-green-50 dark:bg-green-900/20',
    red: 'text-red-500 bg-red-50 dark:bg-red-900/20',
    yellow: 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
    purple: 'text-purple-500 bg-purple-50 dark:bg-purple-900/20',
    cyan: 'text-cyan-500 bg-cyan-50 dark:bg-cyan-900/20',
    emerald: 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
    orange: 'text-orange-500 bg-orange-50 dark:bg-orange-900/20',
    gray: 'text-gray-500 bg-gray-50 dark:bg-gray-900/20',
  };

  return (
    <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-3">
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded ${colorMap[color] || colorMap.gray}`}>{icon}</div>
        <span className="text-xs text-[var(--color-text-tertiary)]">{label}</span>
      </div>
      <div className="text-lg font-bold text-[var(--color-text)]">{value}</div>
    </div>
  );
}

function PurchaseDetailModal({ item, onClose, onApprove, onRetry, onEditCost, onChangeAsin }: {
  item: any; onClose: () => void;
  onApprove: (id: number, asin: string, cost: number) => Promise<void>;
  onRetry: (id: number) => Promise<void>;
  onEditCost: (id: number, cost: number) => Promise<void>;
  onChangeAsin: (id: number, asin: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const can = useCan();
  const canMoney = can('global.view_money');
  const [editingCost, setEditingCost] = useState(false);
  // Sin la llave del dinero el costo real NO se precarga en el campo visible.
  const [costValue, setCostValue] = useState(canMoney ? String(item.unit_cost || '') : '');
  const [editingAsin, setEditingAsin] = useState(false);
  const [asinValue, setAsinValue] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  const isEditable = ['pending_approval', 'safeguard_hold', 'cancelled', 'needs_review'].includes(item.amazon_status);
  const isError = item.amazon_status === 'error';

  const handleApprove = async () => { setActionLoading('approve'); await onApprove(item.id, item.asin, Number(item.unit_cost || 0)); setActionLoading(''); };
  const handleRetry = async () => { setActionLoading('retry'); await onRetry(item.id); setActionLoading(''); };
  const handleSaveCost = async () => {
    const c = parseFloat(costValue);
    if (isNaN(c) || c < 0) return;
    setActionLoading('cost'); await onEditCost(item.id, c); setEditingCost(false); setActionLoading('');
  };
  const handleChangeAsin = async () => {
    if (!asinValue.trim()) return;
    setActionLoading('asin'); await onChangeAsin(item.id, asinValue); setEditingAsin(false); setActionLoading('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h3 className="font-bold text-[var(--color-text)]">{t('amz.purchaseDetail')}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Product */}
          <div className="flex gap-4">
            {item.product_image ? (
              <img src={item.product_image} alt="" className="w-20 h-20 rounded-lg object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-lg bg-[var(--color-bg-tertiary)] flex items-center justify-center">
                <Package size={24} className="text-[var(--color-text-tertiary)]" />
              </div>
            )}
            <div>
              <p className="font-medium text-[var(--color-text)]">{item.product_title || item.asin}</p>
              <div className="flex items-center gap-2 mt-1">
                <EntityLink type="asin" value={item.asin} />
                {item.product_brand && <span className="text-xs text-[var(--color-text-secondary)]">{item.product_brand}</span>}
              </div>
              <StatusBadge status={AMZ_STATUS_MAP[item.amazon_status] || 'unknown'} label={item.amazon_status?.replace(/_/g, ' ')} />
            </div>
          </div>

          {/* Order Info */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            <DetailRow label={t('amz.mlOrder')} value={<EntityLink type="ml_order" value={String(item.ml_order_id)} />} />
            {item.amazon_order_id && <DetailRow label={t('amz.order')} value={<EntityLink type="amazon_order" value={item.amazon_order_id} />} />}
            {item.tracking_number && <DetailRow label={t('amz.tracking')} value={<EntityLink type="tracking" value={item.tracking_number} />} />}
            {item.carrier && <DetailRow label={t('amz.carrier')} value={item.carrier} />}
            <DetailRow label={t('amz.linkSource')} value={item.link_source || '—'} />
            <DetailRow label={t('common.quantity')} value={item.quantity} />
          </div>

          {/* Financials — bloque de puro dinero */}
          {canMoney && (
          <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-3">
            <h4 className="text-xs font-medium text-[var(--color-text-secondary)] mb-2 uppercase">Financiero</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {/* Editable cost */}
              <div>
                <span className="text-[var(--color-text-tertiary)] text-xs">{t('amz.unitCost')}</span>
                {editingCost ? (
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className="text-sm text-[var(--color-text)]">$</span>
                    <input type="number" step="0.01" value={costValue} onChange={(e) => setCostValue(e.target.value)}
                      className="w-20 px-1 py-0.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm text-[var(--color-text)]"
                      autoFocus onKeyDown={(e) => e.key === 'Enter' && handleSaveCost()} />
                    <button onClick={handleSaveCost} disabled={actionLoading === 'cost'} className="p-0.5 text-green-500 hover:text-green-400">
                      {actionLoading === 'cost' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    </button>
                    <button onClick={() => setEditingCost(false)} className="p-0.5 text-[var(--color-text-tertiary)] hover:text-[var(--color-text)]"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-sm text-[var(--color-text)]">
                    {item.unit_cost ? `$${Number(item.unit_cost).toFixed(2)}` : '—'}
                    {can('amazon.edit_cost') && <button onClick={() => { setCostValue(String(item.unit_cost || '')); setEditingCost(true); }}
                      className="p-0.5 text-[var(--color-text-tertiary)] hover:text-[var(--color-primary)]" title="Editar costo">
                      <Pencil size={12} />
                    </button>}
                  </div>
                )}
              </div>
              <DetailRow label={t('amz.totalCost')} value={item.total_cost ? `$${Number(item.total_cost).toFixed(2)}` : '—'} />
              <DetailRow label={t('amz.salePrice')} value={item.sale_price ? `$${Number(item.sale_price).toFixed(2)}` : '—'} />
              <DetailRow label={t('amz.grossProfit')} value={
                item.gross_profit != null
                  ? <span className={Number(item.gross_profit) >= 0 ? 'text-green-500' : 'text-red-500'}>
                      ${Number(item.gross_profit).toFixed(2)}
                    </span>
                  : '—'
              } />
              <DetailRow label={t('amz.profitMargin')} value={
                item.profit_margin != null
                  ? <span className={`font-bold ${Number(item.profit_margin) >= 20 ? 'text-green-500' : Number(item.profit_margin) >= 10 ? 'text-yellow-500' : 'text-red-500'}`}>
                      {Number(item.profit_margin).toFixed(1)}%
                    </span>
                  : '—'
              } />
              {item.exchange_rate && <DetailRow label="Exchange Rate" value={Number(item.exchange_rate).toFixed(4)} />}
            </div>
          </div>
          )}

          {/* Change ASIN */}
          {isEditable && can('amazon.edit_cost') && (
            <div className="border border-[var(--color-border)] rounded-lg p-3">
              <h4 className="text-xs font-medium text-[var(--color-text-secondary)] mb-2 uppercase">Cambiar ASIN</h4>
              {editingAsin ? (
                <div className="flex items-center gap-2">
                  <input type="text" value={asinValue} onChange={(e) => setAsinValue(e.target.value.toUpperCase())}
                    placeholder="B0XXXXXXXX" maxLength={12}
                    className="flex-1 px-2 py-1.5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded text-sm font-mono text-[var(--color-text)]"
                    autoFocus onKeyDown={(e) => e.key === 'Enter' && handleChangeAsin()} />
                  <button onClick={handleChangeAsin} disabled={actionLoading === 'asin' || !asinValue.trim()}
                    className="px-3 py-1.5 bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-white rounded text-sm font-medium disabled:opacity-50">
                    {actionLoading === 'asin' ? <Loader2 size={14} className="animate-spin" /> : 'Guardar'}
                  </button>
                  <button onClick={() => setEditingAsin(false)} className="p-1 text-[var(--color-text-tertiary)] hover:text-[var(--color-text)]"><X size={16} /></button>
                </div>
              ) : (
                <button onClick={() => { setAsinValue(''); setEditingAsin(true); }}
                  className="text-xs text-[var(--color-primary)] hover:underline flex items-center gap-1">
                  <RefreshCw size={12} /> Cambiar ASIN de esta compra
                </button>
              )}
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            <DetailRow label="Creado" value={item.created_at ? new Date(item.created_at).toLocaleString() : '—'} />
            {item.estimated_delivery_start && <DetailRow label="Delivery Est." value={new Date(item.estimated_delivery_start).toLocaleDateString()} />}
            {item.actual_delivery_date && <DetailRow label="Entregado" value={new Date(item.actual_delivery_date).toLocaleDateString()} />}
          </div>

          {item.error_message && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-700 dark:text-red-400">{item.error_message}</p>
            </div>
          )}

          {/* Action buttons */}
          {(isEditable || isError) && can('amazon.approve') && (
            <div className="flex gap-2 pt-2 border-t border-[var(--color-border)]">
              {isEditable && (
                <button onClick={handleApprove} disabled={actionLoading === 'approve'}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors ${
                    ['cancelled', 'needs_review'].includes(item.amazon_status) ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
                  }`}>
                  {actionLoading === 'approve' ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  {['cancelled', 'needs_review'].includes(item.amazon_status) ? 'Re-comprar' : 'Aprobar y Comprar'}
                </button>
              )}
              {isError && (
                <button onClick={handleRetry} disabled={actionLoading === 'retry'}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
                  {actionLoading === 'retry' ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
                  Reintentar
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductDetailModal({ product, onClose }: { product: any; onClose: () => void }) {
  const { t } = useTranslation();
  const canMoney = useCan()('global.view_money');
  const features = product.keepa_features || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-lg max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <h3 className="font-bold text-[var(--color-text)]">{t('amz.productDetail')}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex gap-4">
            {product.main_image ? (
              <img src={product.main_image} alt="" className="w-24 h-24 rounded-lg object-cover" />
            ) : (
              <div className="w-24 h-24 rounded-lg bg-[var(--color-bg-tertiary)] flex items-center justify-center">
                <Package size={28} className="text-[var(--color-text-tertiary)]" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <span className="font-mono text-sm text-teal-500 font-medium">{product.asin}</span>
              <p className="text-sm text-[var(--color-text)] mt-1">{product.title || '—'}</p>
              <div className="flex gap-2 mt-2 flex-wrap">
                {product.brand && <span className="px-2 py-0.5 bg-[var(--color-bg-tertiary)] rounded text-xs text-[var(--color-text-secondary)]">{product.brand}</span>}
                {product.amz_is_prime && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded text-xs font-medium">Prime</span>}
                {product.amz_condition && <span className="px-2 py-0.5 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded text-xs">{product.amz_condition}</span>}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <DetailRow label={t('common.price')} value={canMoney && (product.amz_price || product.price) ? `$${Number(product.amz_price || product.price).toFixed(2)}` : '—'} />
            <DetailRow label={t('amz.rating')} value={product.rating ? `${Number(product.rating).toFixed(1)}★ (${product.ratings_total || 0})` : '—'} />
            <DetailRow label={t('amz.brand')} value={product.brand || '—'} />
            <DetailRow label="Categoría" value={product.category || '—'} />
            {product.keepa_model && <DetailRow label="Modelo" value={product.keepa_model} />}
            {product.keepa_color && <DetailRow label="Color" value={product.keepa_color} />}
            {product.keepa_size && <DetailRow label="Tamaño" value={product.keepa_size} />}
            {product.weight_kg && <DetailRow label="Peso" value={`${product.weight_kg} kg`} />}
            {product.keepa_monthly_sold && <DetailRow label="Ventas/mes" value={product.keepa_monthly_sold} />}
            <DetailRow label="Actualizado" value={product.fetched_at ? new Date(product.fetched_at).toLocaleString() : '—'} />
          </div>

          {features.length > 0 && (
            <div>
              <h4 className="text-xs font-medium text-[var(--color-text-secondary)] mb-2 uppercase">Features</h4>
              <ul className="space-y-1">
                {features.slice(0, 5).map((f: string, i: number) => (
                  <li key={i} className="text-xs text-[var(--color-text)] flex gap-2">
                    <span className="text-[var(--color-text-tertiary)]">•</span>
                    <span className="line-clamp-2">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-[var(--color-text-tertiary)] text-xs">{label}</span>
      <div className="text-[var(--color-text)] text-sm">{value}</div>
    </div>
  );
}
